import * as React from "react";
import { Card, CardContent } from "../atoms/card.js";
import { Typography } from "../atoms/typography.js";
import { cx } from "../internal/cx.js";

export type AuthLayoutVariant = "centered" | "split";
export type AuthLayoutContainer = "default" | "auth" | "narrower";
export type AuthLayoutAlign = "center" | "start";

export interface AuthLayoutProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** Brand/logo slot. */
  brand?: React.ReactNode;
  /** Optional visual panel for split layouts. */
  aside?: React.ReactNode;
  /** Auth screen title. */
  title?: React.ReactNode;
  /** Supporting copy below title. */
  description?: React.ReactNode;
  /** Footer copy/links below the card. */
  footer?: React.ReactNode;
  /** Centered card or split-screen layout. */
  variant?: AuthLayoutVariant;
  /** Width cap for the centered card stack. */
  container?: AuthLayoutContainer;
  /** Alignment for brand, intro text, and footer inside the card stack. */
  align?: AuthLayoutAlign;
}

/**
 * Page-level shell for sign-in / sign-up / recovery flows.
 *
 * Renders a centered card stack by default (brand → card → footer). Switch
 * to `variant="split"` to add a marketing/visual aside on the left for
 * desktop while preserving the centered card behavior on mobile.
 */
export const AuthLayout = React.forwardRef<HTMLDivElement, AuthLayoutProps>(function AuthLayout(
  {
    align = "center",
    aside,
    brand,
    children,
    className,
    container = "default",
    description,
    footer,
    title,
    variant = "centered",
    ...props
  },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cx(
        "cw-v2-theme",
        "cw-v2-auth-layout",
        `cw-v2-auth-layout--${variant}`,
        `cw-v2-auth-layout--align-${align}`,
        `cw-v2-auth-layout--container-${container}`,
        className,
      )}
      {...props}
    >
      {variant === "split" && aside ? (
        <aside className="cw-v2-auth-layout__aside">{aside}</aside>
      ) : null}
      <main className="cw-v2-auth-layout__main">
        <div className="cw-v2-auth-layout__stack">
          {brand ? <div className="cw-v2-auth-layout__brand">{brand}</div> : null}
          <Card elevated>
            <CardContent>
              <div className="cw-v2-auth-layout__card-content">
                {title || description ? (
                  <header className="cw-v2-auth-layout__intro">
                    {title ? (
                      <Typography
                        as="h1"
                        className="cw-v2-auth-layout__title"
                        variant="heading-large"
                      >
                        {title}
                      </Typography>
                    ) : null}
                    {description ? (
                      <Typography className="cw-v2-auth-layout__description" muted>
                        {description}
                      </Typography>
                    ) : null}
                  </header>
                ) : null}
                {children}
              </div>
            </CardContent>
          </Card>
          {footer ? <div className="cw-v2-auth-layout__footer">{footer}</div> : null}
        </div>
      </main>
    </div>
  );
});
AuthLayout.displayName = "AuthLayout";
